import React from 'react';

interface PricingCalculatorProps {
  selectedPackage: 'webapp' | 'app' | 'both';
  timeline: '2weeks' | '1month' | 'superfast';
  addons: {
    inlogRegistratie: boolean;
    androidIOS: boolean;
    extraIntegraties: boolean;
    seoOptimalisatie: boolean;
    googleAds: boolean;
    appStorePublicatie: boolean;
    betaalIntegratie: boolean;
    pushNotificaties: boolean;
    pages5to10: boolean;
    pages10plus: boolean;
    adminDashboard: boolean;
    emailFlows: boolean;
  };
  setAddons: (addons: any) => void;
  onNext: () => void;
}

const PricingCalculator: React.FC<PricingCalculatorProps> = ({
  selectedPackage,
  timeline,
  addons,
  setAddons,
  onNext,
}) => {
  const basePrice = {
    webapp: 795,
    app: 995,
    both: 1250,
  };

  const calculateTotal = () => {
    let total = basePrice[selectedPackage];

    // Add addon prices dynamically based on current addons list
    addonsList.forEach((addon) => {
      if (addons[addon.id as keyof typeof addons]) {
        total += addon.price;
      }
    });

    // Apply timeline multiplier
    if (timeline === '2weeks') {
      total *= 1.25; // 25% more for 2 weeks
    } else if (timeline === 'superfast') {
      total *= 1.5; // 50% more for 1 week (super fast)
    }

    return total;
  };

  const toggleAddon = (addon: keyof typeof addons) => {
    setAddons({
      ...addons,
      [addon]: !addons[addon],
    });
  };

  // Dynamic add-ons based on selected package
  const getAddonsList = () => {
    if (selectedPackage === 'webapp') {
      return [
        { id: 'inlogRegistratie', title: 'Authenticatie & gebruikersaccounts', price: 199 },
        { id: 'betaalIntegratie', title: 'Betaalintegratie', price: 299 },
        { id: 'extraIntegraties', title: 'Diverse externe koppelingen', price: 199 },
        { id: 'adminDashboard', title: 'Beheerders-dashboard', price: 249 },
        { id: 'emailFlows', title: 'Geautomatiseerde e-mailflows', price: 179 },
      ];
    } else if (selectedPackage === 'app') {
      return [
        { id: 'androidIOS', title: 'Android & iOS (beide platforms)', price: 359 },
        { id: 'appStorePublicatie', title: 'App store publicatie', price: 139 },
        { id: 'inlogRegistratie', title: 'Geavanceerde authenticatie', price: 199 },
        { id: 'betaalIntegratie', title: 'Betaalintegratie', price: 299 },
        { id: 'pushNotificaties', title: 'Push notificaties', price: 139 },
      ];
    } else { // both
      return [
        { id: 'androidIOS', title: 'Android & iOS (beide platforms)', price: 359, category: 'app' },
        { id: 'appStorePublicatie', title: 'App store publicatie', price: 139, category: 'app' },
        { id: 'inlogRegistratie', title: 'Geavanceerde authenticatie', price: 199, category: 'app' },
        { id: 'betaalIntegratie', title: 'Betaalintegratie', price: 299, category: 'app' },
        { id: 'pushNotificaties', title: 'Push notificaties', price: 139, category: 'app' },
        { id: 'extraIntegraties', title: 'Diverse externe koppelingen', price: 199, category: 'website' },
        { id: 'seoOptimalisatie', title: 'Basis SEO-optimalisatie', price: 139, category: 'website' },
        { id: 'googleAds', title: 'Integratie met advertentie­platformen', price: 139, category: 'website' },
        { id: 'pages5to10', title: 'Uitbreiding naar 5–10 pagina\'s', price: 249, category: 'website' },
        { id: 'pages10plus', title: 'Uitbreiding naar 10+ pagina\'s', price: 449, category: 'website' },
      ];
    }
  };

  const addonsList = getAddonsList();

  return (
    /* Wrapper with UnderCardBCKGROUND for both cards */
    <div
      className="mt-12 rounded-2xl shadow-lg p-3 sm:p-6 lg:p-8"
      style={{
        backgroundImage: `url('/card-background.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 items-start">
        {/* Main left side - Addon selector */}
        <div className="bg-white/95 rounded-2xl p-4 sm:p-6 flex-1 w-full lg:max-w-2xl">
          <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6">Breid je pakket uit</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Ontdek jouw prijs!</p>

          {/* Timeline speed warning */}
          {(timeline === 'superfast' || timeline === '2weeks') && (
            <div className={`mb-4 p-3 rounded-lg ${timeline === 'superfast' ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'}`}>
              <p className={`text-sm font-semibold ${timeline === 'superfast' ? 'text-red-700' : 'text-orange-700'}`}>
                {timeline === 'superfast' && '⚡ Super snel (1 week): +50% toeslag op alle prijzen'}
                {timeline === '2weeks' && '🚀 Twee weken: +25% toeslag op alle prijzen'}
              </p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <div className="space-y-3">
                {selectedPackage === 'both' ? (
                  <>
                    {/* App Extras */}
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-blue-800 mb-2 uppercase tracking-wide">📱 App Extras</h5>
                      <div className="space-y-3">
                        {addonsList.filter((a: any) => a.category === 'app').map((addon: any) => {
                          let adjustedPrice = addon.price;
                          if (timeline === 'superfast') adjustedPrice = Math.round(addon.price * 1.5);
                          else if (timeline === '2weeks') adjustedPrice = Math.round(addon.price * 1.25);

                          return (
                            <div
                              key={addon.id}
                              onClick={() => toggleAddon(addon.id as keyof typeof addons)}
                              className={`p-3 rounded-lg transition cursor-pointer hover:shadow-md ${
                                addons[addon.id as keyof typeof addons]
                                  ? 'bg-orange-50 border-orange-300'
                                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                              } border space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between`}
                            >
                              {/* Title */}
                              <div className="text-gray-800 text-sm sm:text-base">
                                {addon.title}
                              </div>

                              {/* Price & Toggle - UNDER title on mobile, right side on desktop */}
                              <div className="flex items-center justify-end gap-3 sm:flex-shrink-0">
                                <span className="text-gray-600 text-sm font-semibold whitespace-nowrap">€ {adjustedPrice}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleAddon(addon.id as keyof typeof addons);
                                  }}
                                  className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                                  style={{
                                    background: addons[addon.id as keyof typeof addons]
                                      ? 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)'
                                      : '#d1d5db'
                                  }}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      addons[addon.id as keyof typeof addons]
                                        ? 'translate-x-6'
                                        : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Website Extras */}
                    <div>
                      <h5 className="text-sm font-bold text-blue-800 mb-2 uppercase tracking-wide">💻 Website Extras</h5>
                      <div className="space-y-3">
                        {addonsList.filter((a: any) => a.category === 'website').map((addon: any) => {
                          let adjustedPrice = addon.price;
                          if (timeline === 'superfast') adjustedPrice = Math.round(addon.price * 1.5);
                          else if (timeline === '2weeks') adjustedPrice = Math.round(addon.price * 1.25);

                          return (
                            <div
                              key={addon.id}
                              onClick={() => toggleAddon(addon.id as keyof typeof addons)}
                              className={`p-3 rounded-lg transition cursor-pointer hover:shadow-md ${
                                addons[addon.id as keyof typeof addons]
                                  ? 'bg-orange-50 border-orange-300'
                                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                              } border space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between`}
                            >
                              {/* Title */}
                              <div className="text-gray-800 text-sm sm:text-base">
                                {addon.title}
                              </div>

                              {/* Price & Toggle - UNDER title on mobile, right side on desktop */}
                              <div className="flex items-center justify-end gap-3 sm:flex-shrink-0">
                                <span className="text-gray-600 text-sm font-semibold whitespace-nowrap">€ {adjustedPrice}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleAddon(addon.id as keyof typeof addons);
                                  }}
                                  className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                                  style={{
                                    background: addons[addon.id as keyof typeof addons]
                                      ? 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)'
                                      : '#d1d5db'
                                  }}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      addons[addon.id as keyof typeof addons]
                                        ? 'translate-x-6'
                                        : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  addonsList.map((addon: any) => {
                    let adjustedPrice = addon.price;
                    if (timeline === 'superfast') adjustedPrice = Math.round(addon.price * 1.5);
                    else if (timeline === '2weeks') adjustedPrice = Math.round(addon.price * 1.25);

                    return (
                      <div
                        key={addon.id}
                        onClick={() => toggleAddon(addon.id as keyof typeof addons)}
                        className={`flex items-center justify-between p-3 rounded-lg transition cursor-pointer hover:shadow-md ${
                          addons[addon.id as keyof typeof addons]
                            ? 'bg-orange-50 border-orange-300'
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        } border`}
                      >
                        <span className="text-gray-800 flex-1">{addon.title}</span>
                        <div className="flex items-center space-x-3">
                          <span className="text-gray-600 text-sm">€ {adjustedPrice}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAddon(addon.id as keyof typeof addons);
                          }}
                          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                          style={{
                            background: addons[addon.id as keyof typeof addons]
                              ? 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)'
                              : '#d1d5db'
                          }}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              addons[addon.id as keyof typeof addons]
                                ? 'translate-x-6'
                                : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Total card - sticky positioned on desktop, normal on mobile */}
        <div
          className="rounded-2xl p-4 sm:p-6 lg:p-8 shadow-2xl lg:sticky lg:top-8 flex-shrink-0 w-full lg:w-[420px]"
          style={{
            background: 'linear-gradient(135deg, #0e2a5a 0%, #1f4fa3 50%, #2c66c3 100%)',
          }}
        >

          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Jouw Pakket</h4>

            <div className="flex items-center text-white text-base mb-3">
              <span className="mr-2">📦</span>
              <span>
                {selectedPackage === 'webapp' && 'Webapp'}
                {selectedPackage === 'app' && 'App'}
                {selectedPackage === 'both' && 'Website + App'}
              </span>
            </div>

            {/* Timeline duration */}
            <div className="flex items-center text-white text-base mb-4">
              <span className="mr-2">⏱️</span>
              <span>
                {timeline === '1month' && '1 Maand'}
                {timeline === '2weeks' && '2 Weken'}
                {timeline === 'superfast' && 'Super Snel! (1 Week)'}
              </span>
            </div>

            {/* Included Features */}
            <div className="mb-4">
              <h5 className="text-base font-semibold text-white mb-2">Inbegrepen:</h5>
              <div className="text-sm text-white/90 space-y-1">
                {selectedPackage === 'webapp' && (
                  <>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Toegankelijk vanuit elke browser</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Volledig responsive design (mobiel, tablet & desktop)</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Basis design (UI/UX)</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Basis back-end & database</span></div>
                  </>
                )}
                {selectedPackage === 'app' && (
                  <>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Android of iOS</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Basis design (UI/UX)</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Eenvoudige integraties</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Basis back-end</span></div>
                  </>
                )}
                {selectedPackage === 'both' && (
                  <>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Tot 5 pagina's (website)</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Volledig responsive design</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Contactformulier geïntegreerd</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Android of iOS app</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Basis design (UI/UX)</span></div>
                    <div className="flex items-start"><span className="text-green-400 mr-2">✓</span><span>Basis back-end</span></div>
                  </>
                )}
              </div>
            </div>

            {/* Extras section */}
            <div className="mb-4">
              <h5 className="text-base font-semibold text-white mb-2">Extras:</h5>
              <div className="text-sm text-white/90 space-y-1">
                {addonsList.filter(addon => addons[addon.id as keyof typeof addons]).map(addon => {
                  // Calculate adjusted price based on timeline
                  let adjustedPrice = addon.price;
                  if (timeline === 'superfast') {
                    adjustedPrice = addon.price * 1.5;
                  } else if (timeline === '2weeks') {
                    adjustedPrice = addon.price * 1.25;
                  }

                  return (
                    <div key={addon.id} className="flex items-start justify-between">
                      <div className="flex items-start">
                        <span className="text-orange-400 mr-2">+</span>
                        <span>{addon.title}</span>
                      </div>
                      <span className="text-white/80 ml-2">€{Math.round(adjustedPrice)},-</span>
                    </div>
                  );
                })}
                {addonsList.filter(addon => addons[addon.id as keyof typeof addons]).length === 0 && (
                  <div className="text-white/60 italic">Geen extras geselecteerd</div>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-3">
            <div className="flex justify-between items-baseline mb-5 gap-2">
              <span className="text-base sm:text-lg font-bold text-white whitespace-nowrap">Totaal:</span>
              <span className="text-2xl sm:text-3xl font-bold text-white whitespace-nowrap">€ {calculateTotal().toLocaleString('nl-NL')},-</span>
            </div>

            <button
              onClick={onNext}
              className="w-full text-white py-3 rounded-lg font-semibold text-base transition transform hover:scale-105 flex items-center justify-center"
              style={{
                background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
              }}
            >
              Volgende Stap
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;